import { useQuery } from 'react-query'
import { ProcessingJobsService, type ProcessingJobsParams, type ConflictsParams } from '../services/processingJobs'
import type { ProcessingJobsResponse, DashboardAggregatesResponse, ConflictsResponse, ValidationRulesResponse, IdentityResolutionResponse, ManualReviewErrorsResponse, ManualReviewErrorsParams } from '../types'

/**
 * Hook for fetching processing jobs with pagination
 */
export const useProcessingJobs = (params: ProcessingJobsParams = {}) => {
  const { page = 1, per_page = 10 } = params

  return useQuery<ProcessingJobsResponse, Error>(
    ['processing-jobs', { page, per_page }],
    () => ProcessingJobsService.getProcessingJobsSummary(params),
    {
      keepPreviousData: true, // Keep previous data while fetching new page
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching a specific processing job
 */
export const useProcessingJob = (jobId: string, enabled = true) => {
  return useQuery(
    ['processing-job', jobId],
    () => ProcessingJobsService.getProcessingJob(jobId),
    {
      enabled: !!jobId && enabled,
      staleTime: 30000,
      cacheTime: 300000,
      refetchOnWindowFocus: false,
    }
  )
}

/**
 * Hook for fetching dashboard aggregates
 */
export const useDashboardAggregates = () => {
  return useQuery<DashboardAggregatesResponse, Error>(
    ['dashboard-aggregates'],
    () => ProcessingJobsService.getDashboardAggregates(),
    {
      staleTime: 60000, // Consider data fresh for 1 minute
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching job conflicts with pagination
 */
export const useJobConflicts = (jobId: string | number, params: ConflictsParams = {}, enabled = true) => {
  const { page = 1, per_page = 50, record_id } = params

  return useQuery<ConflictsResponse, Error>(
    ['job-conflicts', jobId, { page, per_page, record_id }],
    () => ProcessingJobsService.getJobConflicts(jobId, params),
    {
      enabled: !!jobId && enabled,
      keepPreviousData: true, // Keep previous data while fetching new page
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching job validation rules
 */
export const useJobValidationRules = (jobId: string | number, enabled = true) => {
  return useQuery<ValidationRulesResponse, Error>(
    ['job-validation-rules', jobId],
    () => ProcessingJobsService.getJobValidationRules(jobId),
    {
      enabled: !!jobId && enabled,
      staleTime: 60000, // Consider data fresh for 1 minute
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching job identity resolution rules
 */
export const useJobIdentityResolution = (jobId: string | number, enabled = true) => {
  return useQuery<IdentityResolutionResponse, Error>(
    ['job-identity-resolution', jobId],
    () => ProcessingJobsService.getJobIdentityResolution(jobId),
    {
      enabled: !!jobId && enabled,
      staleTime: 60000, // Consider data fresh for 1 minute
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}

/**
 * Hook for fetching job manual review errors
 */
export const useJobManualReviewErrors = (jobId: string, params: ManualReviewErrorsParams = {}, enabled = true) => {
  const { pending_only, error_category } = params

  return useQuery<ManualReviewErrorsResponse, Error>(
    ['job-manual-review-errors', jobId, { pending_only, error_category }],
    () => ProcessingJobsService.getJobManualReviewErrors(jobId, params),
    {
      enabled: !!jobId && enabled,
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 300000, // Keep data in cache for 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: 1000,
    }
  )
}